// WbSnapshotThumb — shared snapshot thumbnail for ticket / dashboard /
// future surfaces that render `wb_ticket_snapshots`. Extracted in C11
// from duplicate implementations in WbTicketDetailPage and
// WbTicketRightRail. Mirrors WbDriverDetailPage's signature-image
// pattern: one inline useQuery per image, keyed by the storage key so
// two snapshots with the same key share a cache entry.
//
// N+1 presign debt (flag C9, deferred): this component fires one
// `/files/presigned` request per thumb (and one per full image). Tickets
// with many snapshots — e.g. 8 cameras × 2 events = 16 — generate 16
// requests on detail open. Acceptable at launch but should be batched
// once a `POST /api/v1/files/presigned-batch` endpoint exists on the
// server. Do NOT build a client-side request-queuing workaround — it
// adds complexity without fixing the root cause. When the batch
// endpoint lands, hoist presign resolution into the parent
// (WbTicketDetailPage / WbTicketRightRail), pass URLs down as props,
// and drop both useQuery calls from this component.

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import type { TicketSnapshotDto } from '../hooks/useWbTicket';
import styles from './WbSnapshotThumb.module.css';

interface PresignedResponse {
  key: string;
  url: string;
  expires_at: string;
}

function labelText(label: string): string {
  return label.length === 0 ? label : label[0]!.toUpperCase() + label.slice(1);
}

function formatCaptureTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export interface WbSnapshotThumbProps {
  /**
   * Scope key — usually the ticket enc_id. Used to namespace the React
   * Query cache entry so snapshots from different tickets don't share a
   * presigned URL by accident if their storage keys collide.
   */
  scopeKey: string;
  snapshot: TicketSnapshotDto;
}

export function WbSnapshotThumb({
  scopeKey,
  snapshot,
}: WbSnapshotThumbProps): JSX.Element {
  const thumbKey = snapshot.image_thumb_url ?? snapshot.image_url;

  const thumbQ = useQuery<{ url: string }>({
    queryKey: [
      'wb',
      'snapshot',
      scopeKey,
      snapshot.id,
      'thumb',
      thumbKey,
    ],
    queryFn: async () => {
      const res = await apiClient.get<PresignedResponse>('/files/presigned', {
        params: { key: thumbKey },
      });
      return { url: res.data.url };
    },
    staleTime: 30_000,
    retry: false,
  });

  const fullQ = useQuery<{ url: string }>({
    queryKey: [
      'wb',
      'snapshot',
      scopeKey,
      snapshot.id,
      'full',
      snapshot.image_url,
    ],
    queryFn: async () => {
      const res = await apiClient.get<PresignedResponse>('/files/presigned', {
        params: { key: snapshot.image_url },
      });
      return { url: res.data.url };
    },
    staleTime: 30_000,
    retry: false,
  });

  // Caption: camera name · type (or ANPR plate) · capture time. Falls back
  // gracefully when the camera ref or timestamp is missing.
  const typeText = snapshot.anpr_plate_text ?? labelText(snapshot.label);
  const timeText = formatCaptureTime(snapshot.captured_at);
  const caption = [snapshot.camera?.name, typeText, timeText]
    .filter((part): part is string => typeof part === 'string' && part.length > 0)
    .join(' · ');
  const fullUrl = fullQ.data?.url;

  const inner = (() => {
    if (thumbQ.isLoading) {
      return (
        <span className={styles.placeholder}>
          <Spinner size="sm" label="Loading image" />
        </span>
      );
    }
    if (thumbQ.isError || !thumbQ.data?.url) {
      return <span className={styles.placeholder}>Image unavailable</span>;
    }
    return (
      <img
        className={styles.img}
        src={thumbQ.data.url}
        alt={caption}
        loading="lazy"
      />
    );
  })();

  return (
    <div className={styles.item}>
      {fullUrl ? (
        <a
          className={styles.link}
          href={fullUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {inner}
        </a>
      ) : (
        <div className={styles.link}>{inner}</div>
      )}
      <span className={styles.caption}>{caption}</span>
    </div>
  );
}
